import {
  baseGetMetadata,
  useApp,
  useAuth,
  UseCachedDoc,
  useCachedPractice,
  usePopUpMessage,
} from '@hb/react'
import {
  defaultFormValidate,
  emptyForm,
  emptyStage,
  FieldMapValue,
  Form,
  getFieldFromPath,
  getGynIntroductionForm,
  GynData,
  InsuranceInfo,
  isField,
  isInfoStage,
  Medication,
  mergeData,
  objectToArray,
  Practice,
  FieldMap,
  Supplement,
  UpdateCallback,
  User,
  ValidationErrors,
  WithId,
} from '@hb/shared'
import { FORM_ERROR, FormApi } from 'final-form'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { functions, processFieldMapData } from '../../backend'
import { updateGynAnswers } from '../../backend/functions/gyn'
import { useFirebaseApp } from '../../contexts/FirebaseAppProvider'
import { useSelectedPractice } from '../../hooks/backend/auth'
import { RenderStageProps } from '../forms/renderStage/types'
import { getGynStoragePath, toGynFormData } from './utils'

interface GynFormStatus {
  complete: boolean
  stages: Record<string, { complete: boolean; errors: ValidationErrors }>
}

interface OpenMedication {
  conditionPath: string | null
  medicationId: string
  type: 'medication' | 'supplement'
}

interface ConditionMedications {
  openMedication: OpenMedication | null
  openMedicationConditionModal: (
    type: 'medication' | 'supplement',
    conditionPath: string | null,
    medicationId: string,
  ) => void
  closeMedicationConditionModal: () => void
  selectedMedication: WithId<Medication> | WithId<Supplement> | null
  medicationsArray: Array<WithId<Medication>>
  supplementsArray: Array<WithId<Supplement>>
  selectedCondition: string | null
  handleSubmitMedication: (
    data: FieldMapValue,
    existingMedicationId?: string,
  ) => Promise<ValidationErrors>
  handleDeleteMedication: (
    type: 'medication' | 'supplement',
    medicationId: string,
    conditionPath?: string,
  ) => Promise<void>
  getConditionMedications: (conditionPath: string) => Array<WithId<Medication>>
  getConditionSupplements: (conditionPath: string) => Array<WithId<Supplement>>
}

interface GynCareViewContextData {
  practice: UseCachedDoc<Practice>
  practiceId: string | null
  patient: WithId<User>
  data: FieldMapValue | null | undefined
  corrections: FieldMapValue | null | undefined
  mergedData: FieldMapValue | null | undefined
  onSubmit: (data: FieldMapValue) => Promise<UpdateCallback>
  onStageSubmit: RenderStageProps['onSubmit']
  form: Form
  formData: FieldMapValue | null | undefined
  formCorrections: FieldMapValue | null | undefined
  getForm: (insuranceInfo: InsuranceInfo | undefined) => Form
  formStatus: GynFormStatus
  stages: Array<WithId<FieldMap>>
  canChangePractice: boolean
  admin: boolean
  stageIndex: number
  stage: WithId<FieldMap>
  pendingStageIndex: number | null
  registerStageForm: (form: FormApi | null, formName: string) => void
  callStageSubmit: () => Promise<boolean>
  handleStageSelect: (index: number) => Promise<void>
  setStageIndex: (index: number) => void
  setPendingStageIndex: (index: number | null) => void
  conditionMedicationModal: ConditionMedications
  goNextStage: () => void
}

const emptyUser: WithId<User> = {
  id: '',
  fname: '',
  lname: '',
  email: '',
  isInvite: false,
  createdOn: 0,
  forceUpdateOn: 0,
}

const GynCareViewContext = createContext<GynCareViewContextData>({
  practice: {
    data: null,
    loading: false,
  },
  practiceId: null,
  patient: emptyUser,
  data: null,
  corrections: null,
  mergedData: null,
  onSubmit: async () => ({ error: 'Outside of GynCareViewContext' }),
  onStageSubmit: async () => ({ error: 'Outside of GynCareViewContext' }),
  getForm: () => emptyForm,
  canChangePractice: false,
  admin: false,
  form: emptyForm,
  formData: null,
  formCorrections: null,
  formStatus: { complete: false, stages: {} },
  stages: [],
  stageIndex: 0,
  stage: emptyStage,
  pendingStageIndex: null,
  registerStageForm: () => {},
  callStageSubmit: () => Promise.resolve(false),
  handleStageSelect: async () => {},
  conditionMedicationModal: {
    openMedication: null,
    // tempMedications: {},
    openMedicationConditionModal: () => {},
    closeMedicationConditionModal: () => {},
    selectedMedication: null,
    medicationsArray: [],
    supplementsArray: [],
    selectedCondition: null,
    handleSubmitMedication: async () => ({}),
    handleDeleteMedication: async () => {},
    getConditionMedications: () => [],
    getConditionSupplements: () => [],
  },
  setStageIndex: () => {},
  setPendingStageIndex: () => {},
  goNextStage: () => {},
})

const useConditionMedications = (
  patient: WithId<User>,
  admin: boolean,
  form: Form,
): ConditionMedications => {
  const { appName } = useApp()
  const [openMedication, setOpenMedication] = useState<OpenMedication | null>(null)
  const { functions: customFunctions } = useFirebaseApp()
  const { showError } = usePopUpMessage()
  const medicationsArray = useMemo(
    () => objectToArray(patient.gyn?.medications ?? {}),
    [patient.gyn?.medications],
  )
  const supplementsArray = useMemo(
    () => objectToArray(patient.gyn?.supplements ?? {}),
    [patient.gyn?.supplements],
  )
  // const [tempMedications, setTempMedications] = useState<Record<string, FieldMapValue>>({})

  const selectedCondition = useMemo(() => {
    if (!openMedication) return null
    const field = getFieldFromPath(
      { name: '', children: { ...form.stages } },
      openMedication?.conditionPath ?? '',
    )
    return field && isField(field) ? field.placeholder : null
  }, [openMedication, form])

  const selectedMedication = useMemo(() => {
    if (!openMedication) return null
    const medicationId = openMedication.medicationId
    const medication =
      openMedication.type === 'medication'
        ? patient?.gyn?.medications?.[medicationId]
        : patient?.gyn?.supplements?.[medicationId]
    return medication ? { id: medicationId, ...medication } : null
  }, [openMedication, patient.gyn])

  const handleDeleteMedication = useCallback(
    async (type: 'medication' | 'supplement', medicationId: string, conditionPath?: string) => {
      const deletedMedication =
        type === 'medication'
          ? patient?.gyn?.medications?.[medicationId]
          : patient?.gyn?.supplements?.[medicationId]
      const updatedConditions = conditionPath
        ? deletedMedication?.conditions?.filter(c => c.path !== conditionPath)
        : (deletedMedication?.conditions ?? [])
      const updatedMedication =
        conditionPath && updatedConditions?.length
          ? { ...deletedMedication, conditions: updatedConditions }
          : null
      try {
        await updateGynAnswers(customFunctions ?? functions)({
          patientId: patient.id,
          data: {
            medicationsAndSupplements: {
              [type === 'medication' ? 'medications' : 'supplements']: {
                [medicationId]: updatedMedication,
              },
            },
          },
          appName,
          admin,
        })
      } catch (error: any) {
        const message = error.message ?? 'An error occurred'
        showError(message)
      }
    },
    [appName, admin, patient, showError, customFunctions],
  )

  const handleSubmitMedication = useCallback(
    async (data: FieldMapValue, existingMedicationId?: string) => {
      if (!openMedication) return

      const getExistingMedication = (id: string) =>
        openMedication?.type === 'medication'
          ? patient?.gyn?.medications?.[id]
          : patient?.gyn?.supplements?.[id]

      const existingMedicaiton = existingMedicationId
        ? getExistingMedication(existingMedicationId)
        : null
      const medicationId = existingMedicationId ?? openMedication.medicationId
      const currConditions = existingMedicaiton?.conditions ?? []
      const newConditions =
        !openMedication.conditionPath ||
        currConditions.find(m => m.path === openMedication.conditionPath)
          ? currConditions
          : [
              ...currConditions,
              {
                path: openMedication.conditionPath,
                name: selectedCondition,
              },
            ]
      try {
        await updateGynAnswers(customFunctions ?? functions)({
          patientId: patient.id,
          data: {
            medicationsAndSupplements: {
              [openMedication.type === 'medication' ? 'medications' : 'supplements']: {
                [medicationId]: {
                  ...data,
                  conditions: newConditions,
                },
              },
            },
          },
          appName,
          admin,
        })
        return { success: 'Gyn answers updated' }
      } catch (error: any) {
        const message = error.message ?? 'An error occurred'
        showError(message)
        return { error: message }
      }
    },
    [patient, appName, admin, showError, openMedication, selectedCondition, customFunctions],
  )

  const openMedicationConditionModal = useCallback(
    (type: 'medication' | 'supplement', conditionPath: string | null, medicationId: string) => {
      setOpenMedication({ conditionPath, medicationId, type })
    },
    [],
  )

  const getConditionMedications = useCallback(
    (conditionPath: string) =>
      medicationsArray.filter(m => m.conditions?.some(c => c.path === conditionPath)),
    [medicationsArray],
  )

  const getConditionSupplements = useCallback(
    (conditionPath: string) =>
      supplementsArray.filter(s => s.conditions?.some(c => c.path === conditionPath)),
    [supplementsArray],
  )

  const closeMedicationConditionModal = useCallback(() => {
    setOpenMedication(null)
  }, [])

  return {
    openMedication,
    // tempMedications,
    handleDeleteMedication,
    medicationsArray,
    supplementsArray,
    getConditionMedications,
    getConditionSupplements,
    openMedicationConditionModal,
    handleSubmitMedication,
    closeMedicationConditionModal,
    selectedMedication,
    selectedCondition,
  }
}
export const GynCareViewProvider = ({
  children,
  admin,
  user,
  canChangePractice,
  onSubmitOverride,
}: {
  children: React.ReactNode
  user: WithId<User>
  admin: boolean
  canChangePractice: boolean
  onSubmitOverride?: (data: FieldMapValue) => Promise<{ error?: string; success?: string }>
}) => {
  const authUser = useAuth(s => s.authUser)
  const { id: selectedPracticeId } = useSelectedPractice()
  const { functions: customFunctions, storage: customStorage, auth: customAuth } = useFirebaseApp()
  const mergedData = useMemo<GynData>(
    () =>
      admin ? mergeData(user.gyn?.data ?? {}, user.gyn?.corrections ?? {}) : (user.gyn?.data ?? {}),
    [user.gyn?.data, user.gyn?.corrections, admin],
  )
  const insuranceInfo = useMemo(
    () =>
      admin
        ? mergeData(user.insuranceInfo?.data ?? {}, user.insuranceInfo?.corrections ?? {})
        : (user.insuranceInfo?.data ?? {}),
    [user.insuranceInfo?.data, user.insuranceInfo?.corrections, admin],
  )

  const [stageIndex, setStageIndex] = useState(0)
  const [pendingStageIndex, setPendingStageIndex] = useState<number | null>(null)

  const { appName } = useApp()
  const practiceId = useMemo(() => {
    if (appName === 'providers-app') return selectedPracticeId
    return mergedData?.practiceId ?? null
  }, [mergedData, selectedPracticeId, appName])
  const practice = useCachedPractice(practiceId)

  const getForm = useCallback(
    (insInfo: InsuranceInfo | undefined) => getGynIntroductionForm(admin, insInfo),
    [admin],
  )
  const form = useMemo(() => getForm(insuranceInfo), [getForm, insuranceInfo])
  const stages = useMemo(
    () => objectToArray(form.stages ?? {}).filter(s => !isInfoStage(s)) as Array<WithId<FieldMap>>,
    [form],
  )

  const stage = useMemo(() => stages[stageIndex], [stages, stageIndex])
  const stageForm = useRef<FormApi | null>(null)

  const registerStageForm = useCallback((form: FormApi | null) => {
    stageForm.current = form
  }, [])

  const { showError } = usePopUpMessage()
  const formData = useMemo(() => toGynFormData('data', user), [user])
  const formCorrections = useMemo(() => toGynFormData('corrections', user), [user])
  const validate = useMemo(() => defaultFormValidate(form, false), [form])

  const formStatus = useMemo<GynFormStatus>(() => {
    const errors = validate(formData)
    return {
      complete: !errors,
      stages: Object.entries(form.stages).reduce((acc, [key, stage]) => {
        if (isInfoStage(stage)) return acc
        const stageData = (formData as FieldMapValue)?.[key]
        const stageErrors = errors?.[key]
        return {
          ...acc,
          [key]: {
            errors: stageErrors,
            complete: !stageErrors && !!stageData?.updatedOn,
          },
        }
      }, {}),
    }
  }, [formData, validate, form])

  const conditionMedicationModal = useConditionMedications(user, admin, form)

  const onSubmit = useCallback(
    async (data: FieldMapValue) => {
      try {
        if (onSubmitOverride) {
          return await onSubmitOverride(data)
        }
        await updateGynAnswers(customFunctions ?? functions)({
          patientId: user.id,
          data,
          appName,
          admin,
        })
        return { success: 'Gyn answers updated' }
      } catch (error: any) {
        const message = error.message ?? 'An error occurred'
        showError(message)
        return { error: message }
      }
    },
    [user.id, appName, showError, admin, onSubmitOverride, customFunctions],
  )

  const goNextStage = useCallback(() => {
    setStageIndex(Math.min(stageIndex + 1, stages.length - 1))
  }, [stageIndex, stages, setStageIndex])
  const onStageSubmit = useCallback<RenderStageProps['onSubmit']>(
    async (baseUpdated, onUploadProgress, _form, closeOnSuccess = true) => {
      const effectiveUid = customAuth?.currentUser?.uid ?? authUser?.uid
      if (!effectiveUid) return { [FORM_ERROR]: 'Not logged in' }

      const { ...updated } = baseUpdated ?? {}
      const currentData = formData?.[stage.id as keyof ReturnType<typeof toGynFormData>]

      const processed = await processFieldMapData(
        appName,
        false,
        effectiveUid,
        getGynStoragePath(user.id, stage.id),
        stage,
        updated,
        currentData,
        onUploadProgress,
        null,
        undefined,
        undefined,
        customStorage,
      )

      const metadata = baseGetMetadata(!currentData, effectiveUid, admin ? 'admin' : 'patient')
      const submitted = {
        [stage.id]: {
          ...processed,
          ...metadata,
        },
      }

      const res = await onSubmit(submitted)
      const error = res?.error
      if (error) return { [FORM_ERROR]: error }
      // const newStage =
      //   stage.id === 'insuranceInfo' ? getForm(processed)?.stages?.insuranceInfo : stage
      // const complete = !defaultStageValidate(newStage as Stage, true)(processed)
      if (pendingStageIndex !== null) {
        setStageIndex(pendingStageIndex)
        setPendingStageIndex(null)
      } else if (closeOnSuccess) {
        goNextStage()
      }
      return undefined
    },
    [
      stage,
      onSubmit,
      user.id,
      appName,
      authUser,
      customAuth,
      formData,
      admin,
      goNextStage,
      pendingStageIndex,
      setPendingStageIndex,
      setStageIndex,
      customStorage,
    ],
  )

  const callStageSubmit = useCallback(async () => {
    const currForm = stageForm.current
    if (currForm?.getState().dirty) {
      try {
        const values = currForm.getState().values as FieldMapValue
        const result = await onStageSubmit(values, () => {}, undefined, false)
        if (result?.[FORM_ERROR]) {
          return false
        }
        currForm.reset(values)
        return true
      } catch (error: any) {
        const message = error.message ?? 'An error occurred'
        showError(message)
        return false
      }
    }
    return true
  }, [showError, stageForm, onStageSubmit])

  const handleStageSelect = useCallback(
    async (index: number) => {
      setPendingStageIndex(index)
      const submitted = await callStageSubmit()
      setPendingStageIndex(null)
      if (submitted) {
        setStageIndex(index)
      }
    },
    [setPendingStageIndex, setStageIndex, callStageSubmit],
  )

  const contextData = useMemo(
    () => ({
      practice,
      practiceId,
      patient: user,
      data: user.gyn?.data,
      form,
      getForm,
      formData,
      formCorrections,
      formStatus,
      handleStageSelect,
      stages,
      stage,
      corrections: admin ? user.gyn?.corrections : null,
      mergedData,
      onSubmit,
      onStageSubmit,
      canChangePractice,
      admin,
      stageIndex,
      setStageIndex,
      pendingStageIndex,
      setPendingStageIndex,
      registerStageForm,
      callStageSubmit,
      conditionMedicationModal,
      goNextStage,
    }),
    [
      practice,
      practiceId,
      user,
      mergedData,
      onSubmit,
      onStageSubmit,
      canChangePractice,
      admin,
      handleStageSelect,
      form,
      stages,
      getForm,
      formData,
      formCorrections,
      formStatus,
      stageIndex,
      stage,
      setStageIndex,
      pendingStageIndex,
      setPendingStageIndex,
      registerStageForm,
      callStageSubmit,
      conditionMedicationModal,
      goNextStage,
    ],
  )
  return <GynCareViewContext.Provider value={contextData}>{children}</GynCareViewContext.Provider>
}

export const useGynCareView = () => useContext(GynCareViewContext)
